import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/system';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import InfoIcon from '@mui/icons-material/Info';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardActionArea from '@mui/material/CardActionArea';

import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import {
  MENU_ITEM_NAMES,
  ITEM_DESCRIPTION_AND_PRICES
} from 'styles/constants/appearanceSectionNames';
import PromotionProgress from 'bento-ordering/promotions/components/PromotionProgress';
import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import { useMenu } from 'bento-ordering/context/MenuContext';
import { PROMOTION_DETAILS } from 'bento-ordering/cart/constants/notificationTypes';
import { useIsTabletOrMobile } from 'styles/hooks/mediaQueries';

// these are the two possible orientations that this component can be rendered,
// if there is only 1 promotion then the component is rendered 'horizontal'
// otherwise it is vertical
export const HORIZONTAL = 'horizontal';
export const VERTICAL = 'vertical';

const StyledCard = styled(Card)(({ theme }) => ({
  background: theme.palette.card.background.main,
  // css to create styled bar on left side of card
  '&:before': {
    width: '4px',
    position: 'absolute',
    left: 10,
    top: 10,
    bottom: 10,
    content: '" "',
    background: theme.palette.button.background.hover,
    borderRadius: '2px'
  }
}));

const StyledLocalOfferIcon = styled(LocalOfferIcon)(({ theme }) => ({
  width: '18px',
  fill: theme.palette.button.background.hover,
  '&.MuiSvgIcon-root': {
    verticalAlign: 'middle',
    marginRight: '10px'
  }
}));

const StyledLocalInfoIcon = styled(InfoIcon)({
  width: '18px',
  '&.MuiSvgIcon-root': {
    verticalAlign: 'middle',
    marginLeft: '5px'
  }
});

const StyledCardActionArea = styled(CardActionArea, {
  shouldForwardProp: muiPropForwarding('hasHorizontalStyles')
})(({ hasHorizontalStyles }) => ({
  padding: '20px 15px 15px 29px',
  height: '100%',
  display: 'flex',
  flexDirection: hasHorizontalStyles ? 'row' : 'column',
  justifyContent: 'start',
  alignItems: hasHorizontalStyles ? 'baseline' : 'start'
}));

const StyledBox = styled(Box, {
  shouldForwardProp: muiPropForwarding('hasHorizontalStyles')
})(({ hasHorizontalStyles }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: hasHorizontalStyles ? '10px 20px' : '0 15px 20px 29px',
  alignItems: hasHorizontalStyles ? 'flex-end' : 'start'
}));

const StyledPromotionDescriptionTypography = styled(
  MenuItemDescriptionTypography
)({
  paddingTop: '5px'
});

const PromotionCard = ({ promotion, cardContentOrientation = VERTICAL }) => {
  const id = promotion.id;
  const unlocked = promotion.unlocked;
  const promotionProgressMessage = promotion.promotion_progress_message;
  const {
    title,
    description,
    qualifying_item_details,
    product_promotion_details
  } = promotion.promotion;
  const dispatch = useDispatch();
  const isTabletOrMobile = useIsTabletOrMobile();
  const { createFilteredMenu } = useMenu();

  const openPromotionDetailsModal = () =>
    dispatch(
      openNotificationModal(PROMOTION_DETAILS, {
        id
      })
    );

  const handleOnClick = () => {
    const shouldRenderFilteredItemPage =
      qualifying_item_details.length > 1 ||
      product_promotion_details.length > 1;
    if (shouldRenderFilteredItemPage) {
      createFilteredMenu(promotion);
    } else {
      openPromotionDetailsModal();
    }
  };

  const hasHorizontalStyles =
    cardContentOrientation === HORIZONTAL && !isTabletOrMobile;
  const horizontalStyles = hasHorizontalStyles
    ? {
        flexDirection: 'row',
        alignItems: 'center'
      }
    : {
        flexDirection: 'column'
      };

  return (
    <StyledCard data-testid="promotion-card" onClick={handleOnClick}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          height: '100%',
          cursor: 'pointer',
          ...horizontalStyles
        }}
      >
        <StyledCardActionArea hasHorizontalStyles={hasHorizontalStyles}>
          <MenuItemNameTypography
            variant={MENU_ITEM_NAMES}
            sx={hasHorizontalStyles && { mr: 2 }}
          >
            <StyledLocalOfferIcon />
            {title}
          </MenuItemNameTypography>

          <StyledPromotionDescriptionTypography
            variant={ITEM_DESCRIPTION_AND_PRICES}
          >
            {description}
            <StyledLocalInfoIcon />
          </StyledPromotionDescriptionTypography>
        </StyledCardActionArea>
        <StyledBox hasHorizontalStyles={hasHorizontalStyles}>
          <PromotionProgress
            unlocked={unlocked}
            promotionProgressMessage={promotionProgressMessage}
          />
        </StyledBox>
      </Box>
    </StyledCard>
  );
};

PromotionCard.propTypes = {
  promotion: PropTypes.shape({
    promotion: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      product_promotion_details: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
          id: PropTypes.number
        })
      ).isRequired,
      qualifying_item_details: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
          id: PropTypes.number
        })
      ).isRequired
    }),
    id: PropTypes.number.isRequired,
    unlocked: PropTypes.bool.isRequired,
    amount_before_unlocked: PropTypes.number.isRequired,
    promotion_progress_message: PropTypes.string
  }).isRequired,
  cardContentOrientation: PropTypes.oneOf([HORIZONTAL, VERTICAL])
};
export default PromotionCard;
